import { Link } from 'react-router-dom';
import { publicApi } from '@/services/publicApi';
import { mapListingToCard } from '@/services/listingMapper';
import type { ListingCard } from '@/services/listingMapper';
import { useApiData } from '@/hooks/useApiData';

const UPLOAD_BASE = import.meta.env.VITE_UPLOAD_BASE_URL || import.meta.env.VITE_PUBLIC_API_URL || '';

function buildImageUrl(path: string): string {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('data:')) return path;
  if (path.startsWith('/uploads/')) {
    const base = UPLOAD_BASE.endsWith('/') ? UPLOAD_BASE.slice(0, -1) : UPLOAD_BASE;
    return `${base}${path}`;
  }
  return path;
}

function ListingCardItem({ listing }: { listing: ListingCard }) {
  const imageUrl = listing.images.length > 0
    ? listing.images[0]
    : 'https://readdy.ai/api/search-image?query=Minimal%20clean%20real%20estate%20land%20parcel%20placeholder%2C%20aerial%20view%20of%20green%20plot%20boundary%2C%20soft%20cream%20and%20warm%20sand%20tones%2C%20editorial%20photography%2C%20high%20quality%20professional%20look&width=800&height=600&seq=recent-listing-fallback&orientation=landscape';

  return (
    <Link
      to={`/listings/${listing.id}`}
      className="group rounded-2xl card-dark overflow-hidden hover:border-primary-500/40 transition-all duration-300 flex flex-col"
    >
      <div className="relative w-full h-44 overflow-hidden flex-shrink-0 bg-background-100">
        <img
          src={imageUrl}
          alt={listing.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          onError={(e) => {
            const img = e.currentTarget as HTMLImageElement;
            img.src = 'https://readdy.ai/api/search-image?query=Minimal%20clean%20real%20estate%20land%20parcel%20placeholder%2C%20aerial%20view%20of%20green%20plot%20boundary%2C%20soft%20cream%20and%20warm%20sand%20tones%2C%20editorial%20photography%2C%20high%20quality%20professional%20look&width=800&height=600&seq=recent-listing-fallback&orientation=landscape';
          }}
        />
        <div className="absolute top-3 left-3">
          <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium border ${listing.statusColor}`}>
            {listing.status}
          </span>
        </div>
        <div className="absolute top-3 right-3">
          <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-background-950/60 backdrop-blur-sm text-background-50">
            {listing.category}
          </span>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-1.5 text-xs text-foreground-500 mb-1.5">
          <i className="ri-map-pin-line text-primary-500 text-xs" />
          <span className="truncate">{listing.city || listing.location}</span>
        </div>
        <h3 className="font-display font-semibold text-sm text-foreground-950 mb-2 group-hover:text-primary-500 transition-colors leading-snug line-clamp-2 flex-1">
          {listing.title}
        </h3>
        <div className="grid grid-cols-2 gap-2 mt-auto">
          <div className="p-2 rounded-lg bg-background-200/50 border border-primary-800/20">
            <div className="text-[10px] text-foreground-500">Size</div>
            <div className="text-xs font-semibold text-foreground-950">{listing.sizeDisplay}</div>
          </div>
          <div className="p-2 rounded-lg bg-background-200/50 border border-primary-800/20">
            <div className="text-[10px] text-foreground-500">Rent</div>
            <div className="text-xs font-semibold text-primary-600 truncate">{listing.expectedRentDisplay}</div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-primary-900/20">
          <span className="text-[11px] text-foreground-400 flex items-center gap-1">
            <i className="ri-time-line" />
            {listing.postedDate}
          </span>
          <span className="text-[11px] text-primary-600 font-medium flex items-center gap-1 group-hover:gap-2 transition-all whitespace-nowrap">
            View <i className="ri-arrow-right-line" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl card-dark overflow-hidden animate-pulse">
      <div className="h-44 bg-background-200/50" />
      <div className="p-4 space-y-3">
        <div className="h-3 bg-background-200/50 rounded w-2/3" />
        <div className="h-4 bg-background-200/50 rounded w-full" />
        <div className="h-4 bg-background-200/50 rounded w-4/5" />
        <div className="grid grid-cols-2 gap-2">
          <div className="h-10 bg-background-200/50 rounded-lg" />
          <div className="h-10 bg-background-200/50 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

export default function RecentListingsSection() {
  const { data: apiResponse, loading, error } = useApiData(
    () => publicApi.getListings({ status: 'available', per_page: 12, page: 1, order_by: 't.posted_date DESC' }),
    []
  );

  const listings: ListingCard[] = apiResponse?.data
    ? apiResponse.data.map(mapListingToCard)
    : [];

  const total = apiResponse?.pagination?.total || 0;

  return (
    <section className="py-20 bg-background-50">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
              Recently Listed
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-2">
              Latest Properties
            </h2>
            <p className="text-foreground-600 max-w-lg">
              {total > 0
                ? `${total} verified land parcels listed across Karnataka. Browse the newest additions.`
                : 'Fresh land parcels listed by verified owners across Karnataka. Browse and enquire directly.'}
            </p>
          </div>
          <Link
            to="/listings"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-primary-500/30 text-primary-600 text-sm font-medium hover:bg-primary-500/10 transition-colors whitespace-nowrap shrink-0"
          >
            View All Listings
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 12 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-background-100 border border-primary-500/30 mx-auto mb-4">
              <i className="ri-error-warning-line text-xl text-foreground-400" />
            </div>
            <p className="text-foreground-500 text-sm">Could not load listings right now.</p>
            <Link to="/listings" className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary-600 font-medium hover:text-primary-500 transition-colors">
              Browse all listings <i className="ri-arrow-right-line" />
            </Link>
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-background-100 border border-primary-500/30 mx-auto mb-4">
              <i className="ri-inbox-line text-xl text-primary-500" />
            </div>
            <p className="text-foreground-500 text-sm">No listings yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {listings.map((listing) => (
              <ListingCardItem key={listing.id} listing={listing} />
            ))}
          </div>
        )}

        {/* Bottom CTA bar */}
        {listings.length > 0 && (
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/listings"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Browse All {total > 0 ? `${total}` : ''} Listings
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/submit-property"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-primary-700/40 text-primary-500 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap"
            >
              <i className="ri-add-circle-line" />
              Submit Your Property
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}